import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import { getLocalTime } from 'frontend-utils/utils/dayjs';
import generateOtaLink from 'frontend-utils/utils/url/generate-ota-link';
import HotelTooltipsTooltipRatesTable from 'lumen/components/hotel/tooltips/tooltip-rates-table';
import ParityOtaParityLink from 'lumen/components/parity/ota-parity-link';
import parityClass from 'lumen/helpers/parity-class';
import type { Period } from 'lumen/models';
import type { PeriodParity } from 'lumen/models/period';
import type AjaxService from 'lumen/services/ajax';
import type {
  HotelAndMarketServiceWithHotel,
  HotelOrMarket,
} from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { formatUrl } from 'lumen/utils/rates-request';

export interface TdLowestOnMetaColumn {
  hotel: HotelOrMarket | null;
  isModal?: boolean;
  isParityAnalytics: boolean;
  isParityOtaOutOfSync?: boolean;
  otaId?: string;
  pos?: string;
}

interface ParityTablesCellsTdLowestOnMetaSignature {
  Args: {
    cellData: Period;
    column: TdLowestOnMetaColumn;
  };
}

export default class ParityTablesCellsTdLowestOnMeta extends Component<ParityTablesCellsTdLowestOnMetaSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare liveupdate: Services['liveupdate'];
  @service declare rates: Services['rates'];

  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    return this.period.day;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedLowestRate() {
    return this.rates.formatRate(this.lowestRate, this.hotel, {
      longFormat: false,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    // Hotel is passed as an argument in market/parity/insight/historical
    return this.args.column.hotel ?? this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPast() {
    if (!this.day.date) {
      return false;
    }
    return this.day.date.isBefore(
      getLocalTime(`${this.hotel.hotelinfo.timezone}`),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    if (!this.day.date) {
      return false;
    }

    return this.day.date.isSame(
      getLocalTime(`${this.hotel.hotelinfo.timezone}`),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUpdating() {
    if (this.args.column.pos) {
      return this.liveupdate.isPOSParityUpdating(
        this.period.fromDate,
        this.day.date,
        this.period.parities || [],
        this.args.column.pos,
      );
    }

    return this.liveupdate.isDefaultParityUpdating(
      this.period.fromDate,
      this.day.date,
      this.period.parities || [],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get los() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['los'] ??
        DEFAULT_QP_VALUES.application.los,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lowestRate() {
    const otas = (this.parityDataPerOta as PeriodParity[]) || [];
    // filter out those with position to baserate equal to -3
    // these are irrelevant
    const filteredOtas = otas.filter(
      (ota) => ota.position_to_baserate !== -3 && !ota.is_out_of_sync,
    );

    if (filteredOtas.length === 0) {
      return null;
    }

    // sort on position_to_baserate or difference_with_baserate
    const sortedOtas = filteredOtas.sort((a, b) => {
      const pos = a.position_to_baserate - b.position_to_baserate;
      if (pos === 0) {
        return a.value - b.value;
      }

      return pos;
    });

    if (
      sortedOtas[0] &&
      sortedOtas[0].value !== 0 &&
      sortedOtas[0].position_to_baserate < 0
    ) {
      return sortedOtas[0];
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxPersons() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['maxPersons'] ??
        DEFAULT_QP_VALUES.application.maxPersons,
    );
  }

  /**
   * Parity data for the current cell based on the OTA of the column
   * It's an object when the column is for only one OTA
   * It's an array when the column is for multiple OTAs
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parityDataPerOta() {
    if (this.period.paritiesDict && this.args.column.otaId) {
      return this.period.paritiesDict[this.args.column.otaId] as
        | PeriodParity
        | PeriodParity[];
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get platform() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['platform'] ??
        DEFAULT_QP_VALUES.application.platform,
    ) as Platform;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOTAParityLink() {
    return !this.isPast && !this.args.column.isParityAnalytics;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onDoubleClick() {
    if (!this.access.isStaffUser || !console) {
      return;
    }

    const strConsoleLayout = 'color: #005EA0; font-weight: bold;';

    const formattedDate = this.day?.date.format('ddd l');
    console.group(`Debugging info: metasearch lowest rate - ${formattedDate}`);

    const hotelId = this.hotel?.id;
    const isTripadvisorRebranding = this.hotel?.isTripadvisorRebranding;
    const hotelInfoId = this.hotel?.hotelinfo?.id;

    const hotelInfoURL = `${this.ajax.host}/admin/hotel_industry/hotelinfo/${hotelInfoId}/change/`;
    console.log(`%cHotelInfo: ${hotelInfoURL}`, strConsoleLayout);

    console.groupCollapsed('Metasearch channel rates');

    const metasearchRates: Record<string, PeriodParity> = (
      (this.parityDataPerOta as PeriodParity[]) || []
    ).reduce((acc: Record<string, PeriodParity>, rate) => {
      acc[`${rate.channel}.${rate.ota}`] = rate;
      return acc;
    }, {});
    console.log(metasearchRates);

    console.groupEnd();

    console.groupCollapsed('Debug URLs for OTAs used to scrape the channels');

    const metasearchOTAsMap = (
      (this.parityDataPerOta as PeriodParity[]) || []
    ).reduce((acc, rate) => {
      if (acc.has(rate.ota)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        acc.get(rate.ota).channels.push(rate.channel);
        return acc;
      }

      const fromDate = rate.period.split(',')[0];
      const extractDateUTC = dayjs.utc(rate.extract_datetime);
      const { timezone } = this.hotelAndMarketService;
      const extractDateLocal = extractDateUTC

        .add(timezone, 'hour')
        .format('YYYY-MM-DD');

      const baseURL = `${this.ajax.host}/redirect`;
      const sharedParams = `ota=${rate.ota}&from_date=${fromDate}&hotelinfo_id=${hotelInfoId}&hotel_id=${hotelId}&extract_date=${extractDateLocal}&type='parity'`;

      const scrapedDataURL = encodeURI(
        `${baseURL}/scraped/?${sharedParams}&los=${this.los}&is_new_integrities=true`,
      );

      const fullsURL = encodeURI(
        `${baseURL}/fulls/?${sharedParams}&los=${this.los}&is_new_integrities=true`,
      );

      const ratesURL = encodeURI(`${baseURL}/rates/?${sharedParams}`);

      const multiViewParams = {
        ota: rate.ota,
        los: this.los,
        persons: this.maxPersons,
        membershiptype: rate.membershiptype,
        from_date_range_start: fromDate,
        from_date_range_end: fromDate,
        hotel_id: hotelId,
        'hotelinfo_ids[]': hotelInfoId,
        meta: 'nested',
        type: 'parity',
        platform: this.platform,
      };

      const multiViewURL = encodeURI(
        formatUrl(`https://viewer.${DOMAIN_NAME}/multiview`, multiViewParams),
      );

      const taxConfigParams = {
        subscription_id: hotelId,
        currency: rate.currency,
        shop_currency: rate.shop_currency,
        value: rate.value,
        shop_value: rate.shop_value,
        vat: rate.vat,
        vat_incl: rate.vat_incl,
        city_tax: rate.city_tax,
        city_tax_incl: rate.city_tax_incl,
        other_taxes: rate.other_taxes,
        other_taxes_incl: rate.other_taxes_incl,
        shop_vat_incl: rate.shop_vat_incl,
        shop_other_taxes_incl: rate.shop_other_taxes_incl,
        shop_city_tax_incl: rate.shop_city_tax_incl,
        room_name: rate.room_name,
        cancellation: rate.best_flex, // Should be updated to bar in the future (see OTA-7604)
        extract_datetime: rate.extract_datetime,
        max_persons: rate.max_persons,
        mealtype_included: rate.mealtype_included,
        membershiptype: rate.membershiptype,
        ota: rate.ota,
        hotelinfo: rate.hotelinfo,
        type: 'parity',
        live_url: encodeURIComponent(
          generateOtaLink(
            rate.ota,
            // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string | number'.
            hotelInfoId,
            hotelId,
            isTripadvisorRebranding,
            this.period,
            this.maxPersons,
            'parity',
          ),
        ),
      };
      const taxConfigURL = encodeURI(
        formatUrl(
          '/admin/matching/taxconfig/taxconfig_calculation/',
          taxConfigParams,
          this.ajax.host,
        ),
      );

      let taxConfigMessage = taxConfigURL;
      const oneHourAgo = dayjs.utc().subtract(1, 'hour');
      if (extractDateUTC && extractDateUTC.isBefore(oneHourAgo)) {
        taxConfigMessage = 'only available after performing a live shop';
      }

      acc.set(rate.ota, {
        channels: [rate.channel],
        scrapedDataURL,
        fullsURL,
        ratesURL,
        multiViewURL,
        taxConfigURL: taxConfigMessage,
      });

      return acc;
    }, new Map());

    metasearchOTAsMap.forEach((value, key) => {
      console.groupCollapsed(key);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cChannels:', strConsoleLayout, value.channels);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cScraped data:', strConsoleLayout, value.scrapedDataURL);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cFulls:', strConsoleLayout, value.fullsURL);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cRates:', strConsoleLayout, value.ratesURL);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cMultiview:', strConsoleLayout, value.multiViewURL);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cTax configs:', strConsoleLayout, value.taxConfigURL);
      console.groupEnd();
    });

    console.groupEnd();

    console.groupEnd();
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="flex items-center justify-end truncate tabular-nums
        {{if this.isToday 'td-today'}}"
      {{on "dblclick" this.onDoubleClick}}
    >
      {{#if this.lowestRate}}
        <span
          class={{if
            (not (or this.isUpdating @column.isParityOtaOutOfSync))
            (parityClass this.lowestRate)
          }}
          role="tooltip"
        >
          {{#if this.showOTAParityLink}}
            <ParityOtaParityLink
              @hotel={{this.hotel}}
              @ota={{this.lowestRate.ota}}
              @period={{this.period}}
              @persons={{this.lowestRate.max_persons}}
              @value={{this.formattedLowestRate}}
            />
          {{else}}
            {{this.formattedLowestRate}}
          {{/if}}
          <HotelTooltipsTooltipRatesTable
            @date={{this.day.date}}
            @displayOverModal={{@column.isModal}}
            @fetchOtherLosRates={{false}}
            @hotel={{this.hotel}}
            @isParityAnalytics={{@column.isParityAnalytics}}
            @isUpdating={{this.isUpdating}}
            @noRate={{true}}
            @value={{this.lowestRate}}
          />
        </span>
      {{else}}
        <p>{{t "general.missing"}}</p>
      {{/if}}
    </div>
  </template>
}
